import { useState, useEffect, useRef } from 'react';

const SplashScreen = ({ onDone }) => {
  const [exiting, setExiting] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    // Particles
    const orbs = Array.from({length: 12}, () => ({
      x: Math.random()*W, y: Math.random()*H, r: 20+Math.random()*40,
      speed: 0.2+Math.random()*0.4, alpha: 0.05+Math.random()*0.08
    }));
    const sparkles = Array.from({length: 30}, () => ({
      x: Math.random()*W, y: Math.random()*H, size: 2+Math.random()*4,
      speed: 0.3+Math.random()*0.6, alpha: 0.3+Math.random()*0.5, rot: Math.random()*Math.PI
    }));

    const drawCross = (x, y, s, a) => {
      ctx.save(); ctx.globalAlpha = a; ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x-s,y); ctx.lineTo(x+s,y); ctx.moveTo(x,y-s); ctx.lineTo(x,y+s); ctx.stroke();
      ctx.restore();
    };

    const drawCorner = (cx, cy, sx, sy) => {
      ctx.save(); ctx.strokeStyle = 'rgba(201,168,76,0.15)'; ctx.lineWidth = 1;
      const r1 = Math.abs(40*sx);
      const r2 = Math.abs(60*sx);
      const startAngle = sy > 0 ? 0 : Math.PI;
      const endAngle = sy > 0 ? Math.PI/2 : Math.PI*1.5;
      ctx.beginPath(); ctx.arc(cx,cy,r1,startAngle,endAngle); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx,cy,r2,startAngle,endAngle); ctx.stroke();
      ctx.restore();
    };

    let t = 0;
    const draw = () => {
      t += 0.008;
      ctx.clearRect(0,0,W,H);

      // Light beams
      const cx = W/2, cy = H/2;
      for (let i=0; i<12; i++) {
        const a = (i/12)*Math.PI*2 + t*0.2;
        const grad = ctx.createLinearGradient(cx,cy, cx+Math.cos(a)*500, cy+Math.sin(a)*500);
        grad.addColorStop(0,'rgba(201,168,76,0.04)'); grad.addColorStop(1,'transparent');
        ctx.beginPath(); ctx.moveTo(cx,cy);
        ctx.lineTo(cx+Math.cos(a-0.05)*600, cy+Math.sin(a-0.05)*600);
        ctx.lineTo(cx+Math.cos(a+0.05)*600, cy+Math.sin(a+0.05)*600);
        ctx.fillStyle = grad; ctx.fill();
      }

      // Concentric rings
      [80,140,200,280].forEach((r,i) => {
        ctx.beginPath(); ctx.arc(cx,cy,r+Math.sin(t+i)*5,0,Math.PI*2);
        ctx.strokeStyle = `rgba(201,168,76,${0.06+i*0.02})`; ctx.lineWidth = 1;
        if (i%2) ctx.setLineDash([4,8]); else ctx.setLineDash([]);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      // Orbs
      orbs.forEach(o => {
        o.y -= o.speed; if (o.y < -o.r) { o.y = H+o.r; o.x = Math.random()*W; }
        const grad = ctx.createRadialGradient(o.x,o.y,0,o.x,o.y,o.r);
        grad.addColorStop(0,`rgba(201,168,76,${o.alpha})`); grad.addColorStop(1,'transparent');
        ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(o.x,o.y,o.r,0,Math.PI*2); ctx.fill();
      });

      // Sparkles
      sparkles.forEach(s => {
        s.y -= s.speed; s.rot += 0.02;
        if (s.y < -10) { s.y = H+10; s.x = Math.random()*W; }
        drawCross(s.x, s.y, s.size, s.alpha);
      });

      // Corners
      drawCorner(40,40,1,1); drawCorner(W-40,40,-1,1);
      drawCorner(40,H-40,1,-1); drawCorner(W-40,H-40,-1,-1);

      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', handleResize);

    // Auto dismiss after 7s
    const timer = setTimeout(() => dismiss(), 7000);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => onDone(), 800);
  };

  const letters = 'URBANCIAL'.split('');

  return (
    <div
      className={`fixed inset-0 z-[10001] flex flex-col items-center justify-center cursor-pointer select-none transition-all duration-700 ${exiting ? 'opacity-0 -translate-y-10' : ''}`}
      style={{ background: '#faf7f0' }}
      onClick={dismiss}
      data-testid="splash-screen"
    >
      {/* Background canvas */}
      <canvas ref={canvasRef} className="absolute inset-0" style={{ zIndex: 0 }} />

      {/* Radial glow */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 40%, rgba(201,168,76,0.08) 0%, transparent 60%), radial-gradient(ellipse at 30% 60%, rgba(212,168,40,0.05) 0%, transparent 50%)',
        zIndex: 1
      }} />

      <div className="relative z-10 flex flex-col items-center">
        {/* Building icon */}
        <div className="mb-6" style={{ animation: 'iconFloat 3s ease infinite' }}>
          <svg width="60" height="70" viewBox="0 0 60 70">
            {/* Building bars */}
            {[10,20,30,40].map((x, i) => (
              <rect key={x} x={x} y={70-35-i*5} width="8" height={35+i*5} fill="#8a6a10"
                style={{ transformOrigin: `${x+4}px 70px`, animation: `barUp 0.6s cubic-bezier(.34,1.56,.64,1) ${0.3+i*0.12}s both` }} />
            ))}
            {/* Roof */}
            <polygon points="14,18 30,4 46,18" fill="none" stroke="#c9a84c" strokeWidth="2"
              style={{ transformOrigin: '30px 11px', animation: 'roofPop 0.5s cubic-bezier(.34,1.56,.64,1) 0.9s both' }} />
          </svg>
        </div>

        {/* Wordmark */}
        <div className="flex gap-1 mb-4">
          {letters.map((l, i) => (
            <span key={i} className="inline-block text-3xl md:text-4xl font-semibold tracking-[0.25em]"
              style={{
                fontFamily: "'Josefin Sans', sans-serif",
                color: '#8a6a10',
                animation: `letterUp 0.5s cubic-bezier(.34,1.56,.64,1) ${0.9+i*0.06}s both`
              }}>
              {l}
            </span>
          ))}
        </div>

        {/* Ornamental rule */}
        <div className="flex items-center gap-3 mb-4" style={{ animation: 'ruleReveal 0.5s ease 1.8s both' }}>
          <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,#c9a84c)', animation: 'lineGrow 0.8s ease 1.9s both' }} />
          <div className="w-2 h-2 bg-[#c9a84c]" style={{ animation: 'diamondPop 0.4s ease 2.1s both' }} />
          <div style={{ height: 1, background: 'linear-gradient(90deg,#c9a84c,transparent)', animation: 'lineGrow 0.8s ease 1.9s both' }} />
        </div>

        {/* Tagline */}
        <p className="text-sm tracking-[0.3em] italic opacity-0"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: '#8a6a10', animation: 'fadeIn 0.8s ease 2.3s forwards' }}>
          commercial properties
        </p>

        {/* Click hint */}
        <div className="mt-12 opacity-0 flex flex-col items-center" style={{ animation: 'fadeIn 0.6s ease 3s forwards' }}>
          <div className="w-5 h-8 rounded-full border border-[#c9a84c]/40 flex items-start justify-center pt-1.5 mb-2">
            <div className="w-1 h-2 bg-[#c9a84c]/60 rounded-full" style={{ animation: 'scrollBob 1.5s ease infinite' }} />
          </div>
          <span className="text-[10px] tracking-[0.2em] text-[#8a6a10]/60" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            Click to enter
          </span>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
